import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Extension, Node, mergeAttributes } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import TypographyExtension from '@tiptap/extension-typography';
import { useEditor } from '@tiptap/react';
import { CommentExtension } from '@/components/editor/extensions/comment-mark';
import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useComments } from '@/features/comments/hooks/use-comments';
import { createAgencyTheme } from '@/lib/theme';
import type { NarrativeSummary, ContentDocument } from '@/lib/types';

const CustomParagraph = Node.create({
  name: 'paragraph',
  priority: 1000,
  addOptions: () => ({ HTMLAttributes: {} }),
  group: 'block',
  content: 'inline*',
  parseHTML: () => [{ tag: 'p' }],
  renderHTML({ HTMLAttributes }) {
    return ['p', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },
  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute('data-comment-id'),
        renderHTML: (attrs: Record<string, unknown>) => {
          if (!attrs.commentId) return {};
          return { 'data-comment-id': attrs.commentId, class: 'commented-paragraph' };
        },
      },
    };
  },
});

const ReaderDropCap = Extension.create({
  name: 'readerDropCap',
  addProseMirrorPlugins() {
    return [new Plugin({
      props: {
        decorations(state) {
          let decorations: Decoration[] | null = null;
          state.doc.descendants((node, position) => {
            if (decorations || node.type.name !== 'paragraph') return !decorations;
            const firstLetterOffset = node.textContent.search(/[^\s\u00a0]/u);
            if (firstLetterOffset < 0) return true;
            const from = position + 1 + firstLetterOffset;
            decorations = [
              ...(firstLetterOffset > 0
                ? [Decoration.inline(position + 1, from, { class: 'reader-leading-indent' })]
                : []),
              Decoration.inline(from, from + 1, { class: 'reader-drop-cap' }),
            ];
            return false;
          });
          return decorations ? DecorationSet.create(state.doc, decorations) : DecorationSet.empty;
        },
      },
    })];
  },
});

export function useChapterReaderLogic(novelId: string, chapterId: string) {
  const navigate = useNavigate();
  const { user } = useFirebase();
  const { novel, isLoading: loadingNovel, setSelectedNovelId } = useNovel();
  const { contentDao } = useNovelRepository();

  const [fontSize, setFontSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [fontFamily, setFontFamily] = useState<'serif' | 'sans-serif'>('serif');
  const [themeMode, setThemeMode] = useState<'light' | 'dark' | 'sepia'>('sepia');
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [chapterContent, setChapterContent] = useState('');
  const [isLoadingContent, setIsLoadingContent] = useState(true);

  // Sync Novel Selection
  useEffect(() => {
    if (novelId) setSelectedNovelId(novelId);
  }, [novelId, setSelectedNovelId]);

  // Load theme settings from localStorage with ePlotera namespace
  useEffect(() => {
    const storedTheme = localStorage.getItem('eplotera_reader_theme');
    const storedSize = localStorage.getItem('eplotera_reader_font_size');
    const storedFamily = localStorage.getItem('eplotera_reader_font_family');
    if (storedTheme === 'light' || storedTheme === 'dark' || storedTheme === 'sepia') {
      setThemeMode(storedTheme);
    }
    if (storedSize === 'small' || storedSize === 'medium' || storedSize === 'large') {
      setFontSize(storedSize);
    }
    if (storedFamily === 'serif' || storedFamily === 'sans-serif') {
      setFontFamily(storedFamily);
    }
  }, []);

  const activeTheme = useMemo(() => createAgencyTheme(themeMode === 'sepia' ? 'light' : themeMode), [themeMode]);

  // List of sorted chapters
  const chaptersList = useMemo(() => {
    return Object.values(novel.summaries.chapters)
      .filter(ch => !ch.isArchive && (ch.publishStatus === 'preview' || ch.publishStatus === 'public'))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [novel]);

  const chapterIndex = chaptersList.findIndex(ch => ch.id === chapterId);
  const currentChapter = chaptersList[chapterIndex];
  const previousChapter = chaptersList[chapterIndex - 1];
  const nextChapter = chaptersList[chapterIndex + 1];

  const mainContentId = currentChapter?.mainContentId;

  // Comments hook integration
  const { comments, addComment, addReply, deleteComment } = useComments(mainContentId || null);

  // Fetch chapter prose content
  useEffect(() => {
    let active = true;
    const fetchContent = async () => {
      if (!user?.uid || !novel.id || !mainContentId || !contentDao) {
        setIsLoadingContent(false);
        return;
      }
      setIsLoadingContent(true);
      try {
        const docRef = await contentDao.getContent<ContentDocument>(novel.ownerId, novel, mainContentId);
        if (docRef && active) {
          setChapterContent(docRef.scratchpad || '');
        }
      } catch (err) {
        console.error('Failed to load chapter content:', err);
      } finally {
        if (active) setIsLoadingContent(false);
      }
    };
    fetchContent();
    return () => { active = false; };
  }, [novel, mainContentId, contentDao, user?.uid]);

  // Read-only Tiptap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ paragraph: false }),
      CustomParagraph,
      Underline,
      Highlight,
      TypographyExtension,
      CommentExtension,
      ReaderDropCap,
    ],
    content: chapterContent,
    editable: false,
    immediatelyRender: false,
  });

  useEffect(() => {
    if (editor && chapterContent) {
      editor.commands.setContent(chapterContent);
    }
  }, [chapterContent, editor]);

  // Comment selections
  const handleSaveComment = async (commentText: string) => {
    if (!addComment || !editor || !user?.uid || !novel.id || !mainContentId || !contentDao) return;
    const { from, to } = editor.state.selection;
    const selectionText = from === to ? '' : editor.state.doc.textBetween(from, to);

    const commentId = await addComment(commentText);
    if (commentId) {
      editor.setEditable(true);
      if (from === to) {
        const { $from } = editor.state.selection;
        const pos = $from.before($from.depth);
        const node = $from.node($from.depth);
        if (node && node.type.name === 'paragraph') {
          editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, commentId }));
        }
      } else {
        editor.chain().focus().setComment(commentId).run();
      }
      editor.setEditable(false);

      await contentDao.updateContent(novel.ownerId, novel, mainContentId, {
        scratchpad: editor.getHTML(),
      });
    }
  };

  const handleSaveReply = async (commentId: string, replyText: string) => {
    if (addReply) await addReply(commentId, replyText);
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!deleteComment || !editor || !user?.uid || !novel.id || !mainContentId || !contentDao) return;

    editor.setEditable(true);
    const { tr } = editor.state;
    let modified = false;

    editor.state.doc.descendants((node, pos) => {
      const mark = node.marks.find(m => m.type.name === 'comment' && m.attrs.commentId === commentId);
      if (mark) {
        tr.removeMark(pos, pos + node.nodeSize, mark.type);
        modified = true;
      }
      if (node.type.name === 'paragraph' && node.attrs && node.attrs.commentId === commentId) {
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, commentId: null });
        modified = true;
      }
    });

    if (modified) editor.view.dispatch(tr);
    editor.setEditable(false);

    await deleteComment(commentId);
    await contentDao.updateContent(novel.ownerId, novel, mainContentId, {
      scratchpad: editor.getHTML(),
    });
  };

  // Sync bookmark and reading progress
  useEffect(() => {
    if (novel.id && chapterId) {
      try {
        const stored = localStorage.getItem('eplotera_reading_progress');
        const progress = stored ? JSON.parse(stored) : {};
        const totalReadable = chaptersList.length;
        const pct = Math.round(((chapterIndex + 1) / (totalReadable || 1)) * 100);

        progress[novel.id] = {
          chapterId,
          percent: pct,
          updatedAt: new Date().toISOString(),
        };
        localStorage.setItem('eplotera_reading_progress', JSON.stringify(progress));
      } catch (e) {
        console.error('Failed to update progress:', e);
      }
    }
  }, [novel.id, chapterId, chapterIndex, chaptersList]);

  // User comments permissions
  const canManageAllComments = useMemo(() => {
    if (!novel.id || !user) return false;
    if (novel.ownerId === user.uid) return true;
    return !!novel.collaborators?.[user.uid];
  }, [novel, user]);

  return {
    navigate,
    user,
    novel,
    loadingNovel,
    fontSize,
    setFontSize,
    fontFamily,
    setFontFamily,
    themeMode,
    setThemeMode,
    activeTheme,
    isBookmarked,
    setIsBookmarked,
    settingsAnchor,
    setSettingsAnchor,
    drawerOpen,
    setDrawerOpen,
    isLoadingContent,
    chaptersList,
    chapterIndex,
    currentChapter,
    previousChapter,
    nextChapter,
    comments,
    editor,
    handleSaveComment,
    handleSaveReply,
    handleDeleteComment,
    canManageAllComments,
  };
}
